/**
 * Jerarquía de roles (RN-ROL-01/02).
 * El gating de UI es cosmético: lo que protege es esta matriz aplicada en la API.
 */
import { USER_ROLES } from "./enums.js";
/**
 * Orden jerárquico de los roles operativos. Mayor número = más alcance.
 * `accountant_readonly` queda FUERA de la escala: es un rol lateral de sólo lectura
 * (RN-ROL-01, PV-82) — no «vale menos que un profesor», vale otra cosa.
 */
export const ROLE_RANK = {
    teacher: 1,
    receptionist: 2,
    branch_manager: 3,
    intersede_coordinator: 4,
    admin: 5,
    owner: 6,
};
/** Roles que participan de la jerarquía (todos menos el contador). */
export const RANKED_ROLES = Object.keys(ROLE_RANK);
/** El contador sólo-lectura: nunca alcanza un mínimo jerárquico, se pide explícito. */
export const LATERAL_ROLES = ["accountant_readonly"];
export function isRankedRole(role) {
    return role in ROLE_RANK;
}
export function rankOf(role) {
    return isRankedRole(role) ? ROLE_RANK[role] : 0;
}
/**
 * `roleAtLeast(role, min)` — ¿`role` alcanza el mínimo `min`?
 * `accountant_readonly` devuelve `false` siempre: si un endpoint lo admite,
 * lo declara por lista explícita (`requireRole(["accountant_readonly", ...])`).
 */
export function roleAtLeast(role, min) {
    if (!isRankedRole(role))
        return false;
    return ROLE_RANK[role] >= ROLE_RANK[min];
}
/** Los roles que satisfacen un mínimo — útil para destinatarios de alertas. */
export function rolesAtLeast(min) {
    return RANKED_ROLES.filter((r) => roleAtLeast(r, min));
}
/** RN-ROL-02: ve la facturación TOTAL del club (consolidada, mensual). */
export function canSeeTotalRevenue(role) {
    return role === "owner" || role === "admin";
}
/** RN-ROL-02: ve la facturación DEL DÍA de su alcance. */
export function canSeeDailyRevenue(role) {
    return roleAtLeast(role, "receptionist");
}
/** RN-ROL-13: consulta el registro de auditoría. */
export function canReadAuditLog(role) {
    return role === "owner" || role === "admin";
}
/** RN-ROL-03 / PV-77: edita `settings` y `catalogs` (Administración sólo lee). */
export function canEditSettings(role) {
    return role === "owner" || role === "intersede_coordinator";
}
/** RN-PLA-10/20: edita configuración financiera (precios, promos, tarifario). */
export function canEditFinancialConfig(role) {
    return role === "owner" || role === "admin";
}
/**
 * RN-CON-04: quién emite a demanda el comprobante de un cobro («Facturar»). Facturar el
 * cobro es parte de cobrar, así que lo hace todo el que opera recepción (RN-ROL-02):
 * Dueño, Administración, Coordinador intersedes (RN-ROL-04), Encargado y Recepción. El
 * Contador lee comprobantes, no los emite (RN-ROL-01). Fuente única para la API
 * (`POST /api/invoices`) y para la confirmación de «Cobrar en mostrador».
 */
export const INVOICE_ISSUER_ROLES = [
    "owner",
    "admin",
    "intersede_coordinator",
    "branch_manager",
    "receptionist",
];
export function canIssueInvoice(role) {
    return INVOICE_ISSUER_ROLES.includes(role);
}
/** RN-ROL-02: crea usuarios, asigna sedes, desactiva. */
export function canManageUsers(role) {
    return role === "owner" || role === "admin";
}
/** Un rol acotado a sus sedes asignadas (D6). El coordinador intersedes ve todas. */
export function isBranchScoped(role) {
    return role === "branch_manager" || role === "receptionist" || role === "teacher";
}
export const ALL_ROLES = USER_ROLES;
//# sourceMappingURL=roles.js.map